// apiClient.js — base axios instance for all backend calls.
//
// Security note: this module previously `console.log`ged full request/response
// headers and bodies, which exposed auth tokens and user PII in the browser
// console (and in any error-reporting / analytics pipeline that captures
// console output). All non-error logging now requires `REACT_APP_DEBUG=true`
// and is scrubbed of sensitive fields. DO NOT log headers or token values.

import axios from 'axios';
import { server_api } from '../utils/constants';

const baseURL = server_api();
const DEBUG = process.env.REACT_APP_DEBUG === 'true';

const apiClient = axios.create({
  baseURL: baseURL,
  // Send the httpOnly session cookie on all requests. The backend still
  // accepts the header-based token; this enables the cookie-based path
  // without breaking existing flows.
  withCredentials: true,
});

// Set default Content-Type only for non-FormData requests
apiClient.defaults.headers.post['Content-Type'] = 'application/json';
apiClient.defaults.headers.put['Content-Type'] = 'application/json';

// Request interceptor — attaches customer token for customer endpoints,
// ensures an auth token exists for protected endpoints.
apiClient.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    // Let the browser set multipart Content-Type with its boundary.
    delete config.headers['Content-Type'];
  }

  const token = localStorage.getItem('authToken');
  const customerToken = localStorage.getItem('customerToken');

  const publicEndpoints = [
    '/login/panelLogin',
    '/login/send_otp',
    '/login/verify_otp',
    '/login/fp_sendOTP',
    '/login/fp_verifyOTP',
    '/login/set_password',
    '/api/auth/refresh-token/',
    '/api/auth/signup/',
    '/signup/send_otp',
    '/signup/verify_otp',
    '/api/public/',
    '/api/global/',
  ];

  const customerEndpoints = [
    '/api/customer/',
    '/api/cashier/orders/adds',
  ];

  const url = config.url || '';
  const isPublicEndpoint = publicEndpoints.some(endpoint => url.includes(endpoint));
  const isCustomerEndpoint = customerEndpoints.some(endpoint => url.includes(endpoint));

  if (isCustomerEndpoint && customerToken) {
    config.headers['access_token'] = customerToken;
    return config;
  }

  if (!isPublicEndpoint && !isCustomerEndpoint) {
    if (!token && !localStorage.getItem('refreshToken')) {
      if (DEBUG) console.warn('[apiClient] No auth token; logging out.');
      handleLogout();
      return Promise.reject(new Error('No authentication token available'));
    }
    // Token header is set per-call by ApiServices/*; not here.
  }

  if (DEBUG) {
    // Debug-only, redacted: never log the token value.
    console.debug('[apiClient] request', {
      url: config.url,
      method: config.method,
      hasAuthToken: !!token,
    });
  }
  return config;
});

// URLs where a 401 is a legitimate credential failure (bad password, wrong OTP)
// and must NOT trigger an auto-logout. Everything else that comes back 401
// with an invalid/missing-token message means the session is no longer valid
// and the user must be bounced to login.
const AUTH_ENDPOINTS = [
  '/login/',
  '/signup/',
  '/api/auth/refresh-token/',
];

const isAuthEndpoint = (url) => {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((p) => url.includes(p));
};

// Messages the backend throws for a stale/undecryptable/missing token. These
// all map to SecurityException → HTTP 401 via GlobalExceptionHandler. A user
// seeing any of these on a protected page cannot recover without logging in
// again — so we do it for them rather than surfacing a raw toast.
const STALE_TOKEN_PATTERNS = [
  /invalid token/i,
  /missing token/i,
  /token.*missing/i,
  /token.*expired/i,
  /session.*expired/i,
];

const isStaleTokenMessage = (message) => {
  if (!message) return false;
  if (message.toLowerCase() === 'expired') return true;
  return STALE_TOKEN_PATTERNS.some((re) => re.test(message));
};

// Response interceptor — handles token expiry, stale tokens, and the
// subscription-expired gate.
apiClient.interceptors.response.use(
  (response) => {
    // Some backend endpoints return 200 with a body like {message: "expired"}
    // instead of a proper 401. Treat those the same as an auth failure.
    const msg = response.data?.message;
    if (isStaleTokenMessage(msg) && !isAuthEndpoint(response.config?.url)) {
      handleLogout();
      return Promise.reject({ message: 'expired' });
    }
    return response;
  },
  async (error) => {
    // Subscription-expired gate (402) — surface to the SubscriptionContext.
    if (error?.response?.status === 402
        && error?.response?.data?.code === 'SUBSCRIPTION_EXPIRED') {
      try { window.dispatchEvent(new CustomEvent('rms:subscription-expired')); } catch { /* ignore */ }
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || error.message || 'Unexpected error';
    const onAuthEndpoint = isAuthEndpoint(error.config?.url);

    // Any stale/invalid/missing-token message on a protected endpoint means
    // the session is dead — log the user out instead of leaving them stuck on
    // a page that will just keep erroring. 401 on login/signup is a genuine
    // "wrong password" and must not trigger logout.
    if (!onAuthEndpoint && isStaleTokenMessage(errorMessage)) {
      handleLogout();
      return Promise.reject(error);
    }

    if (DEBUG) {
      console.debug('[apiClient] response error', {
        url: error.config?.url,
        status,
      });
    }
    return Promise.reject(error);
  }
);

function handleLogout() {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('UserRole');
  localStorage.removeItem('UserName');
  localStorage.removeItem('UserMobile');
  localStorage.removeItem('UserId');
  localStorage.removeItem('dark-mode');
  localStorage.removeItem('user');

  window.location.href = '/';
}

export default apiClient;
